import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Branding,
  OnboardingSlot,
  OnboardingVariant,
} from "@/services/branding";
import { OnboardingImageUploader } from "./OnboardingImageUploader";

type ImageKey = keyof Pick<
  Branding,
  | "splashImageUrl"
  | "onboardingImage1Url"
  | "onboardingImage2Url"
  | "onboardingImage3Url"
>;

const SLOTS: Array<{
  key: ImageKey;
  slot: OnboardingSlot;
  label: string;
  hint: string;
}> = [
  {
    key: "splashImageUrl",
    slot: "splash",
    label: "Splash background",
    hint: "Faintly tinted behind the logo on first launch. Any ratio; 1080×1920 or larger works best.",
  },
  {
    key: "onboardingImage1Url",
    slot: "onboarding-1",
    label: "Slide 1 — Find care centres near you",
    hint: "Shown at its own aspect ratio, never cropped. JPEG, PNG, or WebP up to 5MB.",
  },
  {
    key: "onboardingImage2Url",
    slot: "onboarding-2",
    label: "Slide 2 — Compare amenities & prices",
    hint: "Shown at its own aspect ratio, never cropped. JPEG, PNG, or WebP up to 5MB.",
  },
  {
    key: "onboardingImage3Url",
    slot: "onboarding-3",
    label: "Slide 3 — Book in minutes",
    hint: "Shown at its own aspect ratio, never cropped. JPEG, PNG, or WebP up to 5MB.",
  },
];

const VARIANTS: Array<{
  value: OnboardingVariant;
  label: string;
  hint: string;
}> = [
  {
    value: "photo",
    label: "Photo card",
    hint: "Image in a rounded card, headline and body below it on ivory.",
  },
  {
    value: "fullBleed",
    label: "Full bleed",
    hint: "Image fills the screen, copy in white over a dark gradient at the bottom.",
  },
  {
    value: "minimal",
    label: "Minimal (no photo)",
    hint: "Text only on ivory with a large slide numeral. Fastest to load.",
  },
];

interface OnboardingImagesCardProps {
  draft: Branding;
  onChange: (key: ImageKey, url: string | null) => void;
  onVariantChange: (variant: OnboardingVariant) => void;
}

/** Customer-app first-run photos. Empty slots fall back to the bundled image. */
export const OnboardingImagesCard = ({
  draft,
  onChange,
  onVariantChange,
}: OnboardingImagesCardProps) => (
  <Card className="lg:col-span-2">
    <CardHeader>
      <CardTitle>Onboarding screens (customer app)</CardTitle>
      <p className="text-sm text-muted-foreground">
        Photos shown on first launch. Leave a slot on “App default” to keep the
        image bundled with the app. Changes apply on the next app launch after
        saving.
      </p>
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="max-w-md space-y-2">
        <Label htmlFor="onboardingVariant">Layout</Label>
        <Select
          value={draft.onboardingVariant}
          onValueChange={(v) => onVariantChange(v as OnboardingVariant)}
        >
          <SelectTrigger id="onboardingVariant">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VARIANTS.map((v) => (
              <SelectItem key={v.value} value={v.value}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {VARIANTS.find((v) => v.value === draft.onboardingVariant)?.hint}
        </p>
      </div>
      <Separator />
      <div className="grid gap-6 md:grid-cols-2">
        {SLOTS.map((s, i) => (
          <div key={s.key} className="space-y-6">
            <OnboardingImageUploader
              label={s.label}
              hint={s.hint}
              slot={s.slot}
              currentUrl={draft[s.key]}
              onChange={(url) => onChange(s.key, url)}
            />
            {i < SLOTS.length - 2 ? <Separator className="md:hidden" /> : null}
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);
